import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, FlaskConical, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import CommunicationPreferences from '../components/CommunicationPreferences';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Terms & Conditions"
        description="Genora LLC terms and conditions. Research use only policy, ordering terms, and legal information."
        keywords="terms and conditions, research peptide terms, legal policy"
      />
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">Terms & Conditions</h1>
          <p className="text-sm text-slate-500">Last updated: February 2026</p>
          <p className="text-sm text-slate-600 mt-2">Genora LLC - A registered limited liability company</p>
        </div>

        {/* CRITICAL RESEARCH DISCLAIMER */}
        <div className="mb-8 p-6 bg-red-50 border-2 border-red-300 rounded-xl">
          <div className="flex items-start gap-4">
            <AlertTriangle className="h-8 w-8 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="font-heading font-bold text-red-800 text-lg mb-3">⚠️ CRITICAL RESEARCH DISCLAIMER</h3>
              <div className="text-sm text-red-700 space-y-3">
                <p className="font-semibold">
                  ALL PRODUCTS SOLD BY GENORA LLC ARE STRICTLY FOR LABORATORY RESEARCH PURPOSES ONLY.
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>NOT</strong> for human consumption</li>
                  <li><strong>NOT</strong> for animal consumption</li>
                  <li><strong>NOT</strong> for diagnostic or therapeutic use</li>
                  <li><strong>NOT</strong> intended to diagnose, treat, cure, or prevent any disease</li>
                  <li><strong>NOT</strong> approved by the FDA for any purpose other than research</li>
                </ul>
                <p>
                  By purchasing from Genora LLC, you confirm that you are a qualified researcher and that you will 
                  use these products exclusively for legitimate scientific research in compliance with all applicable 
                  laws and regulations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Peptide-Specific Disclaimer */}
        <div className="mb-8 p-6 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-4">
            <FlaskConical className="h-6 w-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-heading font-semibold text-amber-800 mb-2">Research Peptide Notice</h3>
              <p className="text-sm text-amber-700 mb-3">
                The peptides and research compounds sold by Genora LLC are reference materials and research chemicals 
                intended solely for in-vitro laboratory research. These products:
              </p>
              <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                <li>Have not been evaluated by the FDA</li>
                <li>Are not pharmaceutical products</li>
                <li>Should be handled only by qualified research personnel</li>
                <li>Must be stored and disposed of according to applicable safety guidelines</li>
                <li>Are sold without any claims of efficacy for human or animal use</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              By accessing and using the Genora LLC website and purchasing our products, you accept and agree 
              to be bound by these Terms & Conditions. If you do not agree with any part of these terms, you must 
              not use our website or purchase our products.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">2. Product Use Restrictions</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              All products sold by Genora LLC are intended for laboratory research use only. You agree that:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Products will only be used for legitimate scientific research</li>
              <li>Products are NOT for human or animal consumption</li>
              <li>Products are NOT for diagnostic or therapeutic use</li>
              <li>Products are NOT intended to diagnose, treat, cure, or prevent any disease</li>
              <li>You will comply with all applicable laws and regulations</li>
              <li>You will not resell products for non-research purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">3. Purchaser Qualifications</h2>
            <p className="text-slate-600 leading-relaxed">
              By purchasing our products, you represent that you are at least 18 years of age and are purchasing 
              for legitimate research purposes through a qualified research institution, laboratory, or educational facility.
              Genora LLC reserves the right to verify purchaser credentials and refuse sales at our discretion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">4. Orders and Payment</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              All orders are subject to acceptance and availability. We reserve the right to refuse any order 
              for any reason. Payment must be received in full before shipment.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">5. Shipping and Delivery</h2>
            <p className="text-slate-600 leading-relaxed">
              We ship to verified research addresses only. Shipping times are estimates and not guaranteed. 
              Risk of loss and title for products pass to you upon delivery to the carrier.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">6. Returns and Refunds</h2>
            <p className="text-slate-600 leading-relaxed">
              Due to the nature of our products, returns are only accepted for damaged or defective items. 
              Please contact us within 48 hours of delivery if you receive a damaged product.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">7. Disclaimer of Warranties</h2>
            <p className="text-slate-600 leading-relaxed">
              Products are provided "as is" for research purposes. Genora LLC makes no warranties, express or implied, 
              regarding the suitability of our products for any particular purpose beyond legitimate research use.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed">
              Genora LLC shall not be liable for any indirect, incidental, special, consequential, or 
              punitive damages arising from your use of our products or violation of these terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">9. Indemnification</h2>
            <p className="text-slate-600 leading-relaxed">
              You agree to indemnify and hold harmless Genora LLC and its affiliates, officers, directors, and employees 
              from any claims, damages, or expenses arising from your use of our products or violation of these terms.
            </p>
          </section>

          {/* Communications opt-in / opt-out (May 5, 2026) */}
          <section className="mb-8" id="communications">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5 text-teal-700" />
              10. Communication Preferences (Opt-In / Opt-Out)
            </h2>
            <div className="text-slate-600 leading-relaxed space-y-3">
              <p>
                Genora LLC sends two categories of email:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Transactional</strong> — order confirmations, shipping updates, password resets, and other
                  service notices required to fulfill your purchase. These cannot be opted out of while you have an
                  active order.
                </li>
                <li>
                  <strong>Marketing &amp; research updates</strong> — newsletters, product announcements, restock
                  alerts, member discounts, and educational content. These are <strong>opt-in</strong> when you
                  subscribe via the homepage form, sign up for an account with the newsletter box checked, or
                  otherwise expressly request them.
                </li>
              </ul>
              <p>
                You may <strong>opt out</strong> of all marketing emails at any time by clicking the
                <em> Unsubscribe </em> link in the footer of any marketing email, by toggling the preference below
                (if signed in), or by emailing
                {' '}
                <a className="text-teal-700 hover:underline" href="mailto:research@genorapeptides.com">research@genorapeptides.com</a>.
                Opt-out requests are honored within 24 hours.
              </p>
              <p>
                Your email is stored only for the purpose of sending the messages you have requested and is never
                sold or shared with third parties. We retain unsubscribe records to honor your preference even if
                your email is later re-imported.
              </p>
            </div>
            <div className="mt-6">
              <CommunicationPreferences />
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">11. Governing Law</h2>
            <p className="text-slate-600 leading-relaxed">
              These Terms & Conditions shall be governed by and construed in accordance with the laws of the 
              United States of America. Any disputes arising from these terms shall be resolved through binding arbitration.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">12. Contact Information</h2>
            <p className="text-slate-600 leading-relaxed">
              For questions regarding these Terms & Conditions, please{' '}
              <Link to="/contact" className="text-teal-700 hover:underline">contact us</Link>.
            </p>
            <p className="text-slate-600 mt-2">
              Genora LLC<br />
              Email: research@genorapeptides.com
            </p>
          </section>
        </div>

        {/* Final Research Disclaimer */}
        <div className="mt-12 p-6 bg-slate-100 border border-slate-300 rounded-xl">
          <div className="flex items-start gap-4">
            <Shield className="h-6 w-6 text-slate-600 flex-shrink-0" />
            <div>
              <h3 className="font-heading font-semibold text-slate-800 mb-2">Acknowledgment</h3>
              <p className="text-sm text-slate-600">
                By using this website and/or purchasing products from Genora LLC, you acknowledge that you have read, 
                understood, and agree to be bound by these Terms & Conditions and our Research Use Only policy. 
                You confirm that all products purchased will be used solely for legitimate scientific research purposes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
